import React from 'react';
import { Button, Card, Col, Form, Row, Badge } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';

import { Formik } from 'formik';

import { FileUploadFileTooBig } from '@components/Forms';
import { DefaultSpinnerCenter } from '@components/Loader/Loader';
import {
  ProfileDataForm,
  ProfileDefaultField,
  ProfileDocumentsForm,
  ProfileNameField,
} from '@components/Profile/EditProfile';
import { EditProfileInfo } from '@components/Profile/EditProfile/EditProfileInfo';
import { configureProfileQueries } from '@components/Profile/EditProfile/configureQuery';
import { profileFormHandler } from '@components/Profile/EditProfile/handlers';
import {
  DOCUMENT_ADDRESS,
  DOCUMENT_COMPANY_REGISTRATION,
  DOCUMENTS_TO_HIDE,
} from '@config/const';
import { Page500 } from '@containers/ErrorPages';
import Yup from '@features/yupExtensions';
import { profileApi } from '@reducers/profile/profile';

const renderProfileWarning = (plan) => (
  <Badge bg="danger" className="normalFontSize ms-1">
    {plan
      ? 'Please complete the missing data marked in red that is needed for this Plan.'
      : 'To filter required data and documents, please select Country and Plan.'}
  </Badge>
);

const getSpecificDocument = (documentID, documents) =>
  documents.length > 0
    ? documents.filter(({ id }) => id === documentID)[0] || {}
    : {};

const buildValidationSchema = (initialValues, fields, documents) => {
  const tmpSchemas = {};
  fields.forEach(({ field, value, required }) => {
    initialValues[field] = value || '';
    switch (field) {
      case 'email':
        tmpSchemas[field] = Yup.string().betterEmail();
        break;
      case 'phone_number':
        tmpSchemas[field] = Yup.string().phoneNumber();
        break;
      default:
        tmpSchemas[field] = required ? Yup.string().required() : Yup.string();
    }
  });

  documents
    .filter(({ id }) => !DOCUMENTS_TO_HIDE.includes(id))
    .forEach(({ id, value, required }) => {
      initialValues[id] = value || '';
      tmpSchemas[id] = required
        ? FileUploadFileTooBig().required()
        : FileUploadFileTooBig();
    });

  const validationSchema = Yup.object()
    .shape({ ...tmpSchemas })
    .test(
      'both_email_and_phone_number',
      'Both email and phone number are required',
      function testBothIfOneIsEntered(values) {
        if (values.phone_number || values.email) {
          if (!values.email)
            return this.createError({
              path: 'email',
              message:
                'If you enter email you also have to enter phone number!',
            });
          if (!values.phone_number)
            return this.createError({
              path: 'phone_number',
              message:
                'If you enter phone number you also have to enter email!',
            });
        }
        return true;
      }
    );
  return validationSchema;
};

export const EditProfileForm = ({ profileID, data }) => {
  const [searchParams, _] = useSearchParams();
  const filterData = {
    plan: searchParams.get('plan'),
    type: searchParams.get('type'),
  };
  const profileQueries = configureProfileQueries({ id: profileID });
  const profileForm = profileApi.useGetProfileFormDataQuery({
    id: profileID,
    planID: filterData.plan,
    planType: filterData.type,
  });
  if (profileForm.isLoading || profileForm.isFetching) {
    return <DefaultSpinnerCenter />;
  }
  if (profileForm.isError) {
    return <Page500 />;
  }
  const { is_draft: isDraft, fields, documents, templates } = profileForm.data;
  const initialValues = { ...data };

  const validationSchema = buildValidationSchema(
    initialValues,
    fields,
    documents
  );

  const onSubmit = async (values, actions) => {
    const handler = profileFormHandler({
      values,
      actions,
      initialValues,
    });
    handler.submitProfileData(profileQueries.update.query);
    if (isDraft) {
      handler.submitDraftData(profileQueries.addEndUser.query);
    } else {
      handler.submitExistingData(profileQueries.updateEndUser.query);
    }
    handler.submitDocuments(
      profileQueries.uploadDocuments.query,
      documents
        .filter(({ id }) => !DOCUMENTS_TO_HIDE.includes(id))
        .map(({ id }) => id)
    );
  };

  const isLoading =
    profileQueries.updateEndUser.result.isLoading ||
    profileQueries.uploadDocuments.result.isLoading;
  const localDocuments = documents || [];
  const addressDocument = getSpecificDocument(DOCUMENT_ADDRESS, localDocuments);
  const companyRegistrationDocument = getSpecificDocument(
    DOCUMENT_COMPANY_REGISTRATION,
    localDocuments
  );
  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ handleSubmit }) => (
        <Form onSubmit={handleSubmit}>
          <Card className="mb-2">
            <Card.Header className="bg-didxl text-white" />
            <Card.Body>
              <Row>
                <Col md={6}>
                  <ProfileNameField />
                </Col>
                <Col md={6}>
                  <ProfileDefaultField />
                </Col>
              </Row>
            </Card.Body>
          </Card>
          <Card className="mb-2">
            <Card.Header className="bg-didxl text-white">
              Edit end-user profile {renderProfileWarning(filterData.plan)}
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <ProfileDataForm
                    fields={fields}
                    addressDocument={addressDocument}
                  />
                </Col>
                <Col md={6}>
                  <ProfileDocumentsForm
                    documents={documents}
                    templates={templates}
                    profileID={profileID}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Row>
                    <Col md={6}>
                      <Button
                        variant="didxl"
                        size="sm"
                        onClick={handleSubmit}
                        disabled={isLoading}
                      >
                        Save End user
                      </Button>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          <Card>
            <Card.Header className="bg-didxl text-white" />
            <Card.Body>
              <EditProfileInfo
                addressDocument={addressDocument}
                companyRegistrationDocument={companyRegistrationDocument}
              />
            </Card.Body>
          </Card>
        </Form>
      )}
    </Formik>
  );
};
